import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Translate, I18n } from 'react-redux-i18n';
import ListStockSelect from 'components/ListStockSelect';
import MiniLoader from 'components/MiniLoader';
import * as stockActions from 'redux/actions/stockActions';
import * as optionTradeActions from 'redux/actions/optionTradeActions';
import GexChart from './GexChart';
import './Gex.css';

function formatGex(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return '—';
  }
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : n > 0 ? '+' : '';
  if (abs >= 1e9) {
    return `${sign}${(abs / 1e9).toFixed(2)}B`;
  }
  if (abs >= 1e6) {
    return `${sign}${(abs / 1e6).toFixed(2)}M`;
  }
  if (abs >= 1e3) {
    return `${sign}${(abs / 1e3).toFixed(1)}K`;
  }
  return `${sign}${abs.toFixed(0)}`;
}

function formatNumber(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return '—';
  }
  return n.toFixed(digits);
}

function metricTone(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) {
    return '';
  }
  return n > 0 ? 'is-pos' : 'is-neg';
}

const Gex = (props) => {
  const [symbol, setSymbol] = useState('SPY');
  const [symbolName, setSymbolName] = useState('SPDR S&P 500 ETF');
  const [expiration, setExpiration] = useState('');
  const [chartMode, setChartMode] = useState('split');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const reqId = useRef(0);

  const fetchGex = useCallback(async (sym, exp) => {
    const id = ++reqId.current;
    setLoading(true);
    setError(null);
    try {
      const data = await props.loadGex({
        symbol: sym,
        expiration: exp || undefined,
      });
      if (id !== reqId.current) {
        return;
      }
      setPayload(data || null);
      const nextExp = data?.expiration || '';
      if (nextExp && nextExp !== exp) {
        setExpiration(nextExp);
      }
    } catch (err) {
      if (id !== reqId.current) {
        return;
      }
      setError(err?.message || 'error');
      setPayload(null);
    } finally {
      if (id === reqId.current) {
        setLoading(false);
      }
    }
  }, [props.loadGex]);

  useEffect(() => {
    fetchGex('SPY', '');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onClickSelected = (item) => {
    if (!item?.symbol) {
      return;
    }
    const sym = String(item.symbol).toUpperCase();
    setSymbol(sym);
    setSymbolName(item.name || sym);
    setExpiration('');
    props.resetList();
    fetchGex(sym, '');
  };

  const handleChangeSymbol = (event) => {
    const value = event.target.value.trim().toUpperCase();
    setSymbol(value);
    setSymbolName('');
    if (value) {
      props.searchList({ id: props.user_id, search: value }, props.token);
    } else {
      props.resetList();
    }
  };

  const handleSymbolKeyDown = (event) => {
    if (event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    const sym = String(symbol || '').trim().toUpperCase();
    if (!sym) {
      return;
    }
    setSymbol(sym);
    setSymbolName(sym === 'SPX' || sym === 'I:SPX' ? 'S&P 500 Index' : '');
    setExpiration('');
    props.resetList();
    fetchGex(sym, '');
  };

  const handleExpirationChange = (event) => {
    const next = event.target.value;
    setExpiration(next);
    if (symbol) {
      fetchGex(symbol, next);
    }
  };

  const handleReload = () => {
    if (symbol) {
      fetchGex(symbol, expiration);
    }
  };

  const metrics = payload?.metrics || {};
  const strikes = payload?.strikes || [];
  const expirations = payload?.expirations || [];
  const showSearch = symbol.length > 0 && !symbolName;

  const cards = [
    {
      key: 'netGex',
      label: I18n.t('gex_net'),
      value: formatGex(metrics.netGex),
      tone: metricTone(metrics.netGex),
    },
    {
      key: 'totalGamma',
      label: I18n.t('gex_total'),
      value: formatGex(metrics.totalGamma),
      tone: '',
    },
    {
      key: 'gammaFlip',
      label: I18n.t('gex_flip'),
      value: formatNumber(metrics.gammaFlip, metrics.gammaFlip >= 100 ? 0 : 1),
      tone: 'is-flip',
    },
    {
      key: 'putWall',
      label: I18n.t('gex_put_wall'),
      value: formatNumber(metrics.putWall, metrics.putWall >= 100 ? 0 : 1),
      tone: 'is-neg',
    },
    {
      key: 'callWall',
      label: I18n.t('gex_call_wall'),
      value: formatNumber(metrics.callWall, metrics.callWall >= 100 ? 0 : 1),
      tone: 'is-pos',
    },
    {
      key: 'spot',
      label: I18n.t('gex_spot'),
      value: formatNumber(payload?.spot, 2),
      tone: 'is-spot',
    },
    {
      key: 'pcOi',
      label: I18n.t('gex_pc_oi'),
      value: metrics.putCallOi != null ? formatNumber(metrics.putCallOi, 2) : '—',
      tone: '',
    },
    {
      key: 'avgIv',
      label: I18n.t('gex_avg_iv'),
      value: metrics.avgIv != null ? `${(Number(metrics.avgIv) * 100).toFixed(1)}%` : '—',
      tone: '',
    },
  ];

  return (
    <div className="gex-page">
      <div className="gex-toolbar">
        <div className="gex-toolbar-search">
          <label className="gex-label" htmlFor="gex-symbol">
            <Translate value="gex_symbol" />
          </label>
          <div className="gex-toolbar-search-row">
            <div className="symbol-search-wrap symbol-search-wrap--inline">
              <input
                id="gex-symbol"
                name="symbol"
                type="text"
                autoComplete="off"
                className="gex-input"
                value={symbol}
                onChange={handleChangeSymbol}
                onKeyDown={handleSymbolKeyDown}
              />
              {showSearch && (
                <ListStockSelect
                  list={props.searchResults}
                  loading={props.searchLoading}
                  onClickSelected={onClickSelected}
                />
              )}
            </div>
            {symbolName ? (
              <span className="gex-symbol-name">{symbolName}</span>
            ) : null}
          </div>
        </div>

        <div className="gex-toolbar-field">
          <label className="gex-label" htmlFor="gex-expiration">
            <Translate value="gex_expiration" />
          </label>
          <select
            id="gex-expiration"
            className="gex-select"
            value={expiration}
            onChange={handleExpirationChange}
            disabled={!expirations.length}
          >
            {!expirations.length && (
              <option value="">{I18n.t('gex_no_expirations')}</option>
            )}
            {expirations.map((item) => (
              <option key={item.expiration} value={item.expiration}>
                {item.expiration}
                {item.dte != null ? ` (${item.dte}d)` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="gex-mode-toggle" role="group" aria-label={I18n.t('gex_chart_mode')}>
          <button
            type="button"
            className={`gex-mode-btn${chartMode === 'split' ? ' is-active' : ''}`}
            onClick={() => setChartMode('split')}
          >
            <Translate value="gex_mode_split" />
          </button>
          <button
            type="button"
            className={`gex-mode-btn${chartMode === 'net' ? ' is-active' : ''}`}
            onClick={() => setChartMode('net')}
          >
            <Translate value="gex_mode_net" />
          </button>
        </div>

        <button type="button" className="gex-reload-btn" onClick={handleReload} disabled={loading}>
          <Translate value="gex_reload" />
        </button>
      </div>

      {loading && !payload ? (
        <div className="gex-state">
          <MiniLoader />
          <span><Translate value="gex_loading" /></span>
        </div>
      ) : null}

      {error && !loading ? (
        <div className="gex-state gex-state--error">
          <Translate value="gex_error" />
        </div>
      ) : null}

      {!loading && !error && payload && !strikes.length ? (
        <div className="gex-state">
          <Translate value="gex_empty" />
        </div>
      ) : null}

      {payload && strikes.length > 0 ? (
        <>
          {metrics.hasGamma === false ? (
            <div className="gex-banner">
              <Translate value="gex_no_gamma" />
            </div>
          ) : null}

          {metrics.gammaEstimated ? (
            <div className="gex-banner gex-banner--info">
              <Translate value="gex_gamma_estimated" />
            </div>
          ) : null}

          <div className="gex-summary">
            {cards.map((card) => (
              <div key={card.key} className={`gex-card ${card.tone}`.trim()}>
                <div className="gex-card-label">{card.label}</div>
                <div className={`gex-card-value ${card.tone}`.trim()}>{card.value}</div>
              </div>
            ))}
          </div>

          <div className="gex-chart-wrap">
            <div className="gex-chart-header">
              <h3 className="gex-chart-title">
                <Translate value="gex_chart_title" />
              </h3>
              <p className="gex-chart-hint">
                <Translate value="gex_chart_hint" />
              </p>
            </div>
            {loading ? (
              <div className="gex-chart-loading">
                <MiniLoader />
              </div>
            ) : null}
            <GexChart
              strikes={strikes}
              spot={payload.spot}
              gammaFlip={metrics.gammaFlip}
              putWall={metrics.putWall}
              callWall={metrics.callWall}
              mode={chartMode}
            />
          </div>
        </>
      ) : null}
    </div>
  );
};

function mapStateToProps(reducer) {
  return {
    user_id: reducer.session.auth.id,
    token: reducer.session.auth.token,
    searchResults: reducer.stock.list,
    searchLoading: reducer.stock.loading,
  };
}

const mapDispatchToProps = (dispatch) => ({
  searchList: (params, token) => dispatch(stockActions.searchList({ params, token })),
  resetList: () => dispatch(stockActions.resetList({})),
  loadGex: (params) => dispatch(optionTradeActions.loadGex(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Gex);
